/**
 * Owns the agent session lifecycle for the panel: pick/create a session,
 * hold one EventSource open with replay-on-reconnect, expose send/cancel/decide.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { AgentEvent, AgentMode, ProviderId } from '@cortex/shared';
import { Api, ApiError } from '../lib/api';
import { useAgent, pushUserTurn } from '../store/agent';
import { useUi } from '../store/ui';

const LAST_KEY = 'cortex.agent.session';

export function useAgentSession() {
  const sessionId = useAgent((s) => s.sessionId);
  const qc = useQueryClient();
  const toast = useUi((s) => s.toast);
  const esRef = useRef<EventSource | null>(null);

  // Rehydrate the last session on mount.
  useEffect(() => {
    if (sessionId) return;
    const last = localStorage.getItem(LAST_KEY);
    if (!last) return;
    useAgent
      .getState()
      .load(last)
      .catch(() => localStorage.removeItem(LAST_KEY));
  }, [sessionId]);

  // One SSE connection per session, with ?since= replay after a drop.
  useEffect(() => {
    if (!sessionId) return;
    let closed = false;
    let retry = 1000;
    const connect = () => {
      if (closed) return;
      const es = new EventSource(Api.agent.streamUrl(sessionId, useAgent.getState().seq));
      esRef.current = es;
      es.onopen = () => {
        retry = 1000;
        useAgent.getState().setConnected(true);
      };
      const on = (type: AgentEvent['type']) =>
        es.addEventListener(type, (e) => {
          const me = e as MessageEvent<string>;
          const seq = Number(me.lastEventId) || useAgent.getState().seq + 1;
          let ev: AgentEvent;
          try {
            ev = JSON.parse(me.data) as AgentEvent;
          } catch {
            return; // a malformed frame must not take the panel down
          }
          if (!ev || typeof ev.type !== 'string') return;
          useAgent.getState().applyEvent(seq, ev);
          if (ev.type === 'done' || ev.type === 'status') void Api.agent.get(sessionId).then((d) => useAgent.setState({ session: { ...d, messages: undefined, calls: undefined } as never }));
          if (ev.type === 'tool_end' && ev.call.tool === 'kb.propose' && ev.call.status === 'OK') qc.invalidateQueries({ queryKey: ['proposals'] });
        });
      for (const t of ['text_delta', 'thinking', 'tool_start', 'tool_end', 'approval_required', 'citation', 'provider_switched', 'usage', 'message_end', 'status', 'error', 'done'] as const) on(t);
      es.onerror = () => {
        useAgent.getState().setConnected(false);
        es.close();
        if (closed) return;
        setTimeout(connect, retry);
        retry = Math.min(retry * 2, 15_000);
      };
    };
    connect();
    return () => {
      closed = true;
      esRef.current?.close();
      esRef.current = null;
    };
  }, [sessionId, qc]);

  const start = useCallback(
    async (opts: { mode?: AgentMode; provider?: ProviderId; allowRestricted?: boolean } = {}) => {
      const s = await Api.agent.create({ mode: opts.mode ?? useAgent.getState().mode, provider: opts.provider, allowRestricted: opts.allowRestricted ?? false, pinned: [] });
      localStorage.setItem(LAST_KEY, s.id);
      await useAgent.getState().load(s.id);
      return s.id;
    },
    [],
  );

  const open = useCallback(async (id: string) => {
    localStorage.setItem(LAST_KEY, id);
    await useAgent.getState().load(id);
  }, []);

  const send = useCallback(
    async (text: string, ctx: { currentNoteId?: string; selection?: string } = {}) => {
      let id = useAgent.getState().sessionId;
      if (!id) id = await start();
      pushUserTurn(text);
      try {
        await Api.agent.send(id, { text, ...ctx });
      } catch (err) {
        const e = err as ApiError;
        toast(e.message, 'crit');
        await useAgent.getState().load(id);
      }
    },
    [start, toast],
  );

  const cancel = useCallback(async () => {
    const id = useAgent.getState().sessionId;
    if (id) await Api.agent.cancel(id);
  }, []);

  const decide = useCallback(
    async (callId: string, approve: boolean) => {
      const id = useAgent.getState().sessionId;
      if (!id) return;
      try {
        await (approve ? Api.agent.approve(id, callId) : Api.agent.deny(id, callId));
      } catch (err) {
        toast((err as Error).message, 'crit');
      }
    },
    [toast],
  );

  const patch = useCallback(async (p: { mode?: AgentMode; provider?: ProviderId | null; allowRestricted?: boolean; title?: string }) => {
    const id = useAgent.getState().sessionId;
    if (!id) {
      if (p.mode) useAgent.getState().setMode(p.mode);
      return;
    }
    const s = await Api.agent.patch(id, p);
    useAgent.setState({ session: s, mode: s.mode });
  }, []);

  const end = useCallback(async () => {
    const id = useAgent.getState().sessionId;
    if (id) await Api.agent.end(id).catch(() => undefined);
    localStorage.removeItem(LAST_KEY);
    useAgent.getState().reset();
  }, []);

  return { sessionId, start, open, send, cancel, decide, patch, end };
}
